import { Component, OnChanges, OnInit } from '@angular/core';

import { AcuityCategory } from 'src/shared/models/acuity-category.model';
import { AcuityCell } from '../../models/acuity-cell.model';
import { BaseGridComponent } from '../../base-grid/base-grid.component';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { JobCell } from '../../models/job-cell.model';
import { JobTitle } from 'src/shared/models/job-title.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { StaffRatioValue } from 'src/shared/models/staff-ratio-value.model';

@Component({
    selector: 'smt-acuity-grid',
    templateUrl: './acuity-grid.component.html',
    styleUrls: ['./acuity-grid.component.scss']
})
export class AcuityGridComponent extends BaseGridComponent implements OnChanges, OnInit {
    acuityCategories: AcuityCategory[];
    jobTitles: JobTitle[];
    staffingGrid: AcuityCell[][] = [];

    constructor(private _costCenterConfigurationService: CostCenterConfigurationService) { 
        super(_costCenterConfigurationService);

        super.initializeGridProcess(() => this.buildRatioGrid(), () => this.initializeRatios());
    }

    ngOnChanges() {        
        this.acuityCategories = this.costCenter.ancillaryConfiguration.acuityCategories;
        this.jobTitles = this.costCenter.variableJobTitles;

        super.Process();
    }

    ngOnInit() {
    }

    private buildJobCell(id: string, columnId: string, jobTitleId: number, value: number): JobCell {
        let jobCell = new JobCell();

        jobCell.columnId = columnId;
        jobCell.id = id;
        jobCell.jobTitleId = jobTitleId;
        jobCell.rowId = "0";
        jobCell.value = value;
        
        return jobCell;
    }

    buildRatioGrid() : JobCell[][] {
        this.staffingGrid = [];
        
        let ratioGrid: JobCell[][]= [];                
        let columnId = "0";
        
        this.acuityCategories.forEach(a => {
            let ratioRow: JobCell[] = [];
            let staffingGridRow: AcuityCell[] = [];

            this.jobTitles.forEach(j => {            
                let staffCount: number;
                let uosCount: number;
    
                let ratioValue = this.findRatio(j.id, a.id, a.name);
    
                if (ratioValue) {
                    staffCount = ratioValue.staffValue;
                    uosCount = ratioValue.uosCount;
                }
                else {
                    staffCount = null;
                    uosCount = null;
                }
                
                let denominator = this.buildJobCell('denominator-' + a.id + j.id, columnId, j.id, uosCount);
                let numerator = this.buildJobCell('numerator-' + a.id + j.id, columnId, j.id, staffCount);
        
                ratioRow.push(numerator);
                ratioRow.push(denominator);
    
                let cell = new AcuityCell();
                
                cell.acuityCategory = a.name;
                cell.acuityCategoryId = a.id;
                cell.denominator = denominator;
                cell.numerator = numerator;
    
                staffingGridRow.push(cell);
            });
    
            ratioGrid.push(ratioRow);
            this.staffingGrid.push(staffingGridRow);
        });
        

        return ratioGrid;
    }

    buildRatioValues() : StaffRatioValue[] {
        let ratios = [];

        this.acuityCategories.forEach(a => {
            this.jobTitles.forEach(j => {
                let ratio = this.findRatio(j.id, a.id, a.name);
        
                if (!ratio) {                    
                    ratio = new StaffRatioValue();
    
                    ratio.acuityCategoryId = a.id;
                    ratio.acuityCategoryName = a.name;
                    ratio.inactiveDate = null;
                    ratio.jobTitleId = j.id;
                    ratio.shiftScheduleId = this.shiftSchedule.id;  
                    ratio.staffValue = null;
                    ratio.uosCount = null;              
                }
              else {
                    //acuityCategoryId has to be corrected, since findRatio() could have returned wrong acuityCategoryId
                    //also staffRatios might contain inactivated acuityCategoryIds, 
                    //so while building\mapping grids if we use correct acuityCategoryId. Saving grid will take care of using current active acuityCategoryId
                    ratio.acuityCategoryId = a.id;
                }
                ratios.push(ratio);
              });
        });        
    
        return ratios;
    }

    private findAcuityCellByJobCell(cellId: string): AcuityCell { 
        for(let counter = 0; counter < this.staffingGrid.length; counter++) {
            let row = this.staffingGrid[counter];

            if (row.some(c => c.denominator.id == cellId || c.numerator.id == cellId)) {
                return row.find(c => c.denominator.id == cellId || c.numerator.id == cellId);
            }
        }

        return null;
    }

    private findRatio(jobTitleId: number, acuityCategoryId: number, acuityCategoryName: string): StaffRatioValue {
        // Acuity Category IDs may be changed, in which case the staffing grid
        // is "out of sync," and won't take effect until the shift blocks are 
        // updated, like with inpatient.

        // If the acuity categories match, we are editing the live grid.
        if (this.shiftSchedule.staffRatios.some(s => !s.inactiveDate && s.jobTitleId === jobTitleId && s.acuityCategoryId === acuityCategoryId)) {            
            return this.shiftSchedule.staffRatios.find(r => !r.inactiveDate && r.jobTitleId === jobTitleId && r.acuityCategoryId === acuityCategoryId); 
        }

        // If the acuity category IDs do not match, we can attempt to recover values by name.
        return this.shiftSchedule.staffRatios.find(a => !a.inactiveDate && a.acuityCategoryName === acuityCategoryName && a.jobTitleId == jobTitleId);
    }

    private getStaffRatioValueByJobCellId(cell: JobCell): StaffRatioValue {
        let acuityCell = this.findAcuityCellByJobCell(cell.id);
        let ratio: StaffRatioValue;

        if (acuityCell && (acuityCell.acuityCategoryId || acuityCell.acuityCategory)) {
            let foundRatio = this.findRatio(cell.jobTitleId, acuityCell.acuityCategoryId, acuityCell.acuityCategory);

            if (!!foundRatio) {
                ratio = foundRatio;
            }
        }

        if (!ratio) {
            ratio = new StaffRatioValue();
        }

        ratio.acuityCategoryId = ratio.acuityCategoryId;
        ratio.jobTitleId = cell.jobTitleId;

        return ratio;
    }

    private initializeRatios() {
        this.shiftSchedule.staffRatios = this.buildRatioValues();
    }

    updateShiftCell(cell: JobCell) { 
        let ratio = this.getStaffRatioValueByJobCellId(cell);        

        if (MiscUtil.isValidNumber(cell.value)) {
            ratio.staffValue = cell.value;            
        } 
        else {
            ratio.staffValue = null;
        }
    }

    updateUosCell(cell: JobCell) {
        let ratio = this.getStaffRatioValueByJobCellId(cell);

        if (MiscUtil.isValidNumber(cell.value)) {
            ratio.uosCount = cell.value;
        } 
        else {
            ratio.uosCount = null;
        }
    };
}
